import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Grid,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconBriefcase,
  IconBuilding,
  IconEdit,
  IconIdBadge2,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUser,
  IconUsers,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import api from '../api/axiosConfig';

const initialForm = {
  id: null,
  first_name: '',
  last_name: '',
  employee_type: 'OFFICE',
  job_title_id: '',
  position: '',
  phone: '',
  email: '',
  department: '',
  site_name: '',
  is_active: true,
};

function StatCard({ title, value, icon, color = 'blue' }) {
  return (
    <Paper withBorder radius="lg" p="md">
      <Group justify="space-between">
        <div>
          <Text c="dimmed" size="sm">{title}</Text>
          <Text fw={700} size="xl">{value}</Text>
        </div>
        <Box
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--mantine-color-gray-0)',
          }}
        >
          {icon}
        </Box>
      </Group>
    </Paper>
  );
}

export default function HumanResourcesPage() {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);

  const [search, setSearch] = useState('');
  const [employeeType, setEmployeeType] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');

  const [form, setForm] = useState(initialForm);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [employeeModalOpened, setEmployeeModalOpened] = useState(false);
  const [detailDrawerOpened, setDetailDrawerOpened] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [jobTitles, setJobTitles] = useState([]);
  const [jobTitleModalOpened, setJobTitleModalOpened] = useState(false);
  const [jobTitleForm, setJobTitleForm] = useState({
    id: null,
    name: '',
    description: '',
    employee_type: 'ALL',
    is_active: true,
  });
  const [jobTitleSubmitting, setJobTitleSubmitting] = useState(false);

  const fetchOverview = async () => {
    try {
      const res = await api.get('/hr/overview');
      setOverview(res.data);
    } catch (err) {
      console.error('İK özet verileri alınamadı', err);
    }
  };

  const fetchSites = async () => {
    try {
      let res;
      try {
        res = await api.get('/construction-sites/simple-sites');
      } catch (err) {
        if (err.response?.status === 404 || err.response?.status === 405) {
          res = await api.get('/construction-sites');
        } else {
          throw err;
        }
      }

      setSites((res.data || []).map((site) => ({
        value: site.name,
        label: site.name,
      })));
    } catch (err) {
      console.error('Şantiyeler alınamadı', err);
      setSites([]);
    }
  };

  const fetchJobTitles = async (type = null) => {
    try {
      const params = {};
      if (type) params.employee_type = type;

      const res = await api.get('/hr/job-titles', { params });

      const formatted = (res.data || [])
        .filter((item) => item.is_active !== false)
        .map((item) => ({
          value: String(item.id),
          label: item.name,
          raw: item,
        }));

      setJobTitles(formatted);
    } catch (err) {
      console.error('Görev tanımları alınamadı', err);
      setJobTitles([]);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = {};

      if (search.trim()) params.search = search.trim();
      if (employeeType) params.employee_type = employeeType;
      if (statusFilter === 'active') params.is_active = true;
      if (statusFilter === 'passive') params.is_active = false;
      if (accountFilter === 'with_account') params.has_user = true;
      if (accountFilter === 'without_account') params.has_user = false;

      const res = await api.get('/hr/employees', { params });
      setEmployees(res.data || []);
    } catch (err) {
      console.error('Personeller alınamadı', err);
      notifications.show({
        title: 'Hata',
        message: 'Personel listesi alınamadı.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    fetchSites();
    fetchJobTitles();
  }, []);

  useEffect(() => {
    // jobTitles'ı employee_type'a göre filtrele
    if (form.employee_type) {
      fetchJobTitles(form.employee_type);
    }
  }, [form.employee_type]);

  useEffect(() => {
    fetchEmployees();
  }, [search, employeeType, statusFilter, accountFilter]);

  const officeCount = useMemo(
    () => employees.filter((e) => e.employee_type === 'OFFICE').length,
    [employees]
  );

  const siteCount = useMemo(
    () => employees.filter((e) => e.employee_type === 'SITE').length,
    [employees]
  );

  const openCreateModal = () => {
    setForm(initialForm);
    setEmployeeModalOpened(true);
  };

  const openEditModal = (employee) => {
    setForm({
      id: employee.id,
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      employee_type: employee.employee_type || 'OFFICE',
      job_title_id: employee.job_title_id ? String(employee.job_title_id) : '',
      position: employee.position || '',
      phone: employee.phone || '',
      email: employee.email || '',
      department: employee.department || '',
      site_name: employee.site_name || '',
      is_active: employee.is_active ?? true,
    });
    setEmployeeModalOpened(true);
  };

  const openDetailDrawer = async (employee) => {
    try {
      const res = await api.get(`/hr/employees/${employee.id}`);
      setSelectedEmployee(res.data);
      setDetailDrawerOpened(true);
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: 'Personel detayı alınamadı.',
        color: 'red',
      });
    }
  };

  const handleSaveEmployee = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      notifications.show({
        title: 'Uyarı',
        message: 'Ad ve soyad zorunludur.',
        color: 'yellow',
      });
      return;
    }

    if (form.employee_type === 'SITE' && !form.site_name) {
      notifications.show({
        title: 'Uyarı',
        message: 'Şantiye personeli için şantiye seçimi zorunludur.',
        color: 'yellow',
      });
      return;
    }

    if (form.employee_type === 'OFFICE' && !form.department?.trim()) {
      notifications.show({
        title: 'Uyarı',
        message: 'Merkez ofis personeli için departman bilgisi giriniz.',
        color: 'yellow',
      });
      return;
    }

    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      employee_type: form.employee_type,
      job_title_id: form.job_title_id ? Number(form.job_title_id) : null,
      position: form.position?.trim() || null,
      phone: form.phone?.trim() || null,
      email: form.email?.trim() || null,
      department: form.employee_type === 'OFFICE' ? (form.department?.trim() || null) : null,
      site_name: form.employee_type === 'SITE' ? (form.site_name || null) : null,
      is_active: form.is_active,
    };

    setSubmitting(true);
    try {
      if (form.id) {
        await api.put(`/hr/employees/${form.id}`, payload);
      } else {
        await api.post('/hr/employees', payload);
      }

      notifications.show({
        title: 'Başarılı',
        message: form.id ? 'Personel güncellendi.' : 'Personel eklendi.',
        color: 'teal',
      });

      setEmployeeModalOpened(false);
      setForm(initialForm);
      fetchEmployees();
      fetchOverview();
    } catch (err) {
      console.error(err);
      notifications.show({
        title: 'Hata',
        message: err.response?.data?.detail || 'Kayıt işlemi başarısız.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEmployee = (employee) => {
    modals.openConfirmModal({
      title: 'Personeli pasife al',
      centered: true,
      children: (
        <Text size="sm">
          <strong>{employee.first_name} {employee.last_name}</strong> kaydını pasife almak istediğine emin misin?
        </Text>
      ),
      labels: { confirm: 'Pasife Al', cancel: 'İptal' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/hr/employees/${employee.id}`);
          notifications.show({
            title: 'Başarılı',
            message: 'Personel pasife alındı.',
            color: 'teal',
          });
          fetchEmployees();
          fetchOverview();
        } catch (err) {
          notifications.show({
            title: 'Hata',
            message: 'İşlem başarısız.',
            color: 'red',
          });
        }
      },
    });
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2}>İnsan Kaynakları</Title>
          <Text c="dimmed">Merkez ofis ve şantiye personellerini tek ekrandan yönetin.</Text>
        </div>

        <Button leftSection={<IconPlus size={18} />} radius="md" onClick={openCreateModal}>
          Yeni Personel
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard title="Toplam Personel" value={overview?.total_employees ?? 0} icon={<IconUsers size={22} color="#228be6" />} />
        <StatCard title="Aktif Personel" value={overview?.active_employees ?? 0} icon={<IconUser size={22} color="#12b886" />} />
        <StatCard title="Merkez Ofis" value={overview?.office_employees ?? officeCount} icon={<IconBuilding size={22} color="#7950f2" />} />
        <StatCard title="Şantiye Personeli" value={overview?.site_employees ?? siteCount} icon={<IconBriefcase size={22} color="#f08c00" />} />
      </SimpleGrid>

      <Paper withBorder radius="lg" p="md">
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <TextInput
              leftSection={<IconSearch size={16} />}
              label="Ara"
              placeholder="Ad, soyad, telefon, e-posta..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 2.5 }}>
            <Select
              label="Personel Tipi"
              placeholder="Tümü"
              data={[
                { value: '', label: 'Tümü' },
                { value: 'OFFICE', label: 'Merkez Ofis' },
                { value: 'SITE', label: 'Şantiye' },
              ]}
              value={employeeType}
              onChange={setEmployeeType}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 2.5 }}>
            <Select
              label="Durum"
              placeholder="Tümü"
              data={[
                { value: '', label: 'Tümü' },
                { value: 'active', label: 'Aktif' },
                { value: 'passive', label: 'Pasif' },
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 3 }}>
            <Select
              label="Sistem Hesabı"
              placeholder="Tümü"
              data={[
                { value: '', label: 'Tümü' },
                { value: 'with_account', label: 'Hesabı Olanlar' },
                { value: 'without_account', label: 'Hesabı Olmayanlar' },
              ]}
              value={accountFilter}
              onChange={setAccountFilter}
            />
          </Grid.Col>
        </Grid>
      </Paper>

      {/* -- Employees table, modal/drawer code unchanged except position + job_title_id usage -- */}
      <Paper withBorder radius="lg" p="md">
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Personel</Table.Th>
                <Table.Th>Tip</Table.Th>
                <Table.Th>Pozisyon</Table.Th>
                <Table.Th>Departman / Şantiye</Table.Th>
                <Table.Th>İletişim</Table.Th>
                <Table.Th>Sistem Hesabı</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th>İşlem</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {employees.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Text ta="center" c="dimmed" py="md">
                      Kayıt bulunamadı.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                employees.map((employee) => (
                  <Table.Tr key={employee.id}>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text fw={600}>
                          {employee.first_name} {employee.last_name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          #{employee.id}
                        </Text>
                      </Stack>
                    </Table.Td>

                    <Table.Td>
                      <Badge color={employee.employee_type === 'OFFICE' ? 'violet' : 'orange'}>
                        {employee.employee_type === 'OFFICE' ? 'Merkez Ofis' : 'Şantiye'}
                      </Badge>
                    </Table.Td>

                    <Table.Td>{employee.position || (employee.job_title?.name ?? '-')}</Table.Td>

                    <Table.Td>
                      {employee.employee_type === 'OFFICE'
                        ? (employee.department || '-')
                        : (employee.site_name || '-')}
                    </Table.Td>

                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm">{employee.phone || '-'}</Text>
                        <Text size="xs" c="dimmed">{employee.email || '-'}</Text>
                      </Stack>
                    </Table.Td>

                    <Table.Td>
                      {employee.has_user_account ? (
                        <Badge color="teal" variant="light">
                          {employee.user?.username || 'Bağlı'}
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="light">
                          Hesap yok
                        </Badge>
                      )}
                    </Table.Td>

                    <Table.Td>
                      <Badge color={employee.is_active ? 'green' : 'red'}>
                        {employee.is_active ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </Table.Td>

                    <Table.Td>
                      <Group gap={6}>
                        <ActionIcon variant="light" color="blue" onClick={() => openDetailDrawer(employee)}>
                          <IconIdBadge2 size={16} />
                        </ActionIcon>
                        <ActionIcon variant="light" color="orange" onClick={() => openEditModal(employee)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon variant="light" color="red" onClick={() => handleDeleteEmployee(employee)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Modal
        opened={employeeModalOpened}
        onClose={() => setEmployeeModalOpened(false)}
        title={form.id ? 'Personel Düzenle' : 'Yeni Personel Ekle'}
        centered
        size="lg"
        radius="lg"
      >
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Ad"
              required
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            />
            <TextInput
              label="Soyad"
              required
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            />
          </SimpleGrid>

          <Select
            label="Personel Tipi"
            data={[
              { value: 'OFFICE', label: 'Merkez Ofis' },
              { value: 'SITE', label: 'Şantiye' },
            ]}
            value={form.employee_type}
            onChange={(value) =>
              setForm((prev) => ({
                ...prev,
                employee_type: value,
                department: value === 'OFFICE' ? prev.department : '',
                site_name: value === 'SITE' ? prev.site_name : '',
              }))
            }
          />

          <Group align="end" grow>
            <Select
              label="Görev Tanımı"
              placeholder="Görev seçin"
              data={jobTitles}
              value={form.job_title_id}
              onChange={(value) => setForm({ ...form, job_title_id: value || '' })}
              searchable
              clearable
              nothingFoundMessage="Görev bulunamadı"
            />

            <Button
              variant="light"
              onClick={() => {
                setJobTitleForm({
                  id: null,
                  name: '',
                  description: '',
                  employee_type: form.employee_type === 'SITE' ? 'SITE' : 'OFFICE',
                  is_active: true,
                });
                setJobTitleModalOpened(true);
              }}
            >
              Görev Ekle
            </Button>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Telefon"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <TextInput
              label="E-posta"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </SimpleGrid>

          {/* Departman + Pozisyon alanları her iki tip için de gösteriliyor (kullanıcı deneyimi olarak aynı yerde) */}
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Departman"
              placeholder="Örn: Bilgi İşlem"
              value={form.department}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
            />
            <TextInput
              label="Pozisyon"
              placeholder="Örn: Yazılım Geliştirici"
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
            />
          </SimpleGrid>

          {form.employee_type === 'SITE' && (
            <Select
              label="Şantiye"
              placeholder="Şantiye seçin"
              data={sites}
              value={form.site_name}
              onChange={(value) => setForm({ ...form, site_name: value || '' })}
              searchable
              clearable
              nothingFoundMessage="Şantiye bulunamadı"
              required
            />
          )}

          <Switch
            label="Personel aktif"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.currentTarget.checked })}
          />

          <Button loading={submitting} onClick={handleSaveEmployee}>
            {form.id ? 'Güncelle' : 'Kaydet'}
          </Button>
        </Stack>
      </Modal>

      {/* job title modal (aynı) */}
      <Modal
        opened={jobTitleModalOpened}
        onClose={() => setJobTitleModalOpened(false)}
        title="Görev Tanımı Ekle"
        centered
        radius="lg"
      >
        <Stack>
          <TextInput
            label="Görev Adı"
            placeholder="Örn: Şantiye Şefi"
            value={jobTitleForm.name}
            onChange={(e) => setJobTitleForm({ ...jobTitleForm, name: e.target.value })}
          />

          <TextInput
            label="Açıklama"
            placeholder="Opsiyonel açıklama"
            value={jobTitleForm.description}
            onChange={(e) => setJobTitleForm({ ...jobTitleForm, description: e.target.value })}
          />

          <Select
            label="Uygun Personel Tipi"
            data={[
              { value: 'ALL', label: 'Tümü' },
              { value: 'OFFICE', label: 'Merkez Ofis' },
              { value: 'SITE', label: 'Şantiye' },
            ]}
            value={jobTitleForm.employee_type}
            onChange={(value) => setJobTitleForm({ ...jobTitleForm, employee_type: value })}
          />

          <Switch
            label="Aktif"
            checked={jobTitleForm.is_active}
            onChange={(e) => setJobTitleForm({ ...jobTitleForm, is_active: e.currentTarget.checked })}
          />

          <Button
            loading={jobTitleSubmitting}
            onClick={async () => {
              if (!jobTitleForm.name.trim()) {
                notifications.show({
                  title: 'Uyarı',
                  message: 'Görev adı zorunludur.',
                  color: 'yellow',
                });
                return;
              }

              setJobTitleSubmitting(true);
              try {
                await api.post('/hr/job-titles', {
                  name: jobTitleForm.name.trim(),
                  description: jobTitleForm.description?.trim() || null,
                  employee_type: jobTitleForm.employee_type,
                  is_active: jobTitleForm.is_active,
                });

                notifications.show({
                  title: 'Başarılı',
                  message: 'Görev tanımı eklendi.',
                  color: 'teal',
                });

                setJobTitleModalOpened(false);
                // yeni görev eklendikten sonra listeyi güncelle
                await fetchJobTitles(form.employee_type);
              } catch (err) {
                notifications.show({
                  title: 'Hata',
                  message: err.response?.data?.detail || 'Görev tanımı eklenemedi.',
                  color: 'red',
                });
              } finally {
                setJobTitleSubmitting(false);
              }
            }}
          >
            Kaydet
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}